<template>
    <form @submit.prevent="handleSubmit" id="order_create_form">
        <div class="mb-4 mt-2">
            <div class="d-flex justify-content-between align-items-center mb-2">
                <label class="form-label form-label-thin mb-0"> Vendor<span class="text-danger">*</span></label>
                <button type="button" class="btn btn-sm btn-link text-primary text-decoration-none p-0"
                    @click="openCreateVendorModal">
                    <i class="fa fa-plus-circle me-1"></i> Add Vendor
                </button>
            </div>
            <MultiSelect 
                v-model="form.vendor_id" 
                :options="vendorOptions" 
                :multiple="false" 
                placeholder="Select Vendor" 
                id="vendor_id"
                name="vendor_id"
                ref="vendor_id"
                @update:modelValue="onVendorSelected"
            />
            <div v-if="errors.vendor_id" class="text-danger small mt-1">
                {{ errors.vendor_id[0] || errors.vendor_id }}
            </div>
        </div>

        <!-- Modal with CreateVendor inside slot -->
        <FormModal ref="createVendorModal" title="Create New Vendor" id="create_vendor_form_modal">
            <CreateVendor @created="onVendorCreated" @close="closeCreateVendorModal" />
        </FormModal>

        <div class="mb-4">
            <label class="form-label form-label-thin">Order Description</label>
            <textarea class="form-control form-control-thin" v-model="form.description" name="description" id="description" ref="description" rows="2"
                placeholder="Describe what you want to order"></textarea>
        </div>

        <div class="mb-4">
            <div class="d-flex justify-content-between align-items-center mb-2">
                <label class="form-label form-label-thin">Order Items</label>
            </div>

            <div v-for="(item, index) in form.items" :key="index" class="order-item-card mb-4 p-3 border rounded">
                <div class="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
                    <strong>Item {{ index + 1 }}</strong>
                    <button type="button" class="btn btn-sm btn-outline-danger hover-white" @click="removeItem(index)"
                        v-if="form.items.length > 1">
                        <i class="fa fa-times"></i> Remove
                    </button>
                </div>

                <div class="row">
                    <div class="col-md-12 mb-2">
                        <MultiSelect 
                            v-model="item.item_id" 
                            @update:modelValue="value => onItemSelected(item, value)"
                            :multiple="false" 
                            :options="vendorMaterialOptions"
                            placeholder="Select an item" 
                            label="Select Item"  name="item_id" id="item_id" ref="item_id"
                        />
                    </div>

                    <div class="col-md-4 mb-2">
                        <label class="form-label form-label-thin">Quantity</label>
                        <input type="number" min="0" step="1" class="form-control form-control-thin"
                            v-model.number="item.quantity" @input="updateItemTotal(item)" 
                            :name="`items[${index}][quantity]`" placeholder="0" />
                    </div>

                    <div class="col-md-4 mb-2">
                        <label class="form-label form-label-thin">Unit</label>
                        <select class="form-select form-select-thin" v-model="item.unit"
                            :name="`items[${index}][unit]`">
                            <option v-for="unit in units" :key="unit" :value="unit">{{ unit }}</option>
                        </select>
                    </div>

                    <div class="col-md-4 mb-2">
                        <label class="form-label form-label-thin">Unit Price</label>
                        <input type="number" min="0" step="0.01" class="form-control form-control-thin"
                            v-model.number="item.unit_price" @input="updateItemTotal(item)"
                            :name="`items[${index}][unit_price]`" placeholder="0.00" />
                    </div>
                    <div class="col-md-12 mb-2 mt-2">
                        <label class="form-label form-label-thin">Total</label>
                        <input type="text" class="form-control form-control-thin" :value="formatTotal(item.total)"
                            readonly :name="`items[${index}][total]`" />
                    </div>

                    <div v-if="item.material_type === 'chemical'" class="col-md-12 mb-3 mt-3">
                        <div class="row gx-2 compact-row">
                            <div class="col-md-12 mb-3 compact-field">
                                <label class="form-label form-label-thin">Take Safety Data Sheet (SDS) at receiving PO?</label>
                                <select class="form-select form-select-thin" v-model="item.sds"
                                    :name="`items[${index}][sds]`">
                                    <option value="yes">Yes</option>
                                    <option value="no">No</option>
                                </select>
                            </div>
                            <div class="col-md-12 mb-3 compact-field">
                                <label class="form-label form-label-thin">Is it Approved Chemical?</label>
                                <select class="form-select form-select-thin" v-model="item.approved_chemical"
                                    :name="`items[${index}][approved_chemical]`">
                                    <option value="yes">Yes</option>
                                    <option value="no">No</option>
                                </select>
                            </div>
                            <div class="col-md-12 mb-3 compact-field">
                                <label class="form-label form-label-thin">Is it Food Grade chemical?</label>
                                <select class="form-select form-select-thin" v-model="item.food_grade_chemical"
                                    :name="`items[${index}][food_grade_chemical]`">
                                    <option value="yes">Yes</option>
                                    <option value="no">No</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    <div v-if="item.material_type === 'part'" class="col-md-12 mb-3 mt-3">
                        <label class="form-label form-label-thin">Is it Food Contact part?</label>
                        <select class="form-select form-select-thin" v-model="item.food_contact_part"
                            :name="`items[${index}][food_contact_part]`">
                            <option value="yes">Yes</option>
                            <option value="no">No</option>
                        </select>
                    </div>

                    
                </div>
            </div>
            <div class="d-flex justify-content-between align-items-center mb-2">
                <label class="form-label form-label-thin"></label>
                <button type="button" class="btn btn-sm btn-primary hover-white" @click="addItem">
                    <i class="fa fa-plus"></i> Add Item
                </button>
            </div>

            <div class="mb-4 d-flex justify-content-between align-items-center flex-wrap">
                <div class="order-total-display w-100 text-center py-2 mb-2">
                    <div class="small">Order Grand Total</div>
                    <div class="h4 mb-0">{{ formatTotal(orderTotal) }}</div>
                </div>
            </div>
        </div>

        <div class="mb-4">
            <label class="form-label form-label-thin">Expected Delivery Date</label>
            <input type="date" class="form-control form-control-thin" v-model="form.expected_delivery_date" id="expected_delivery_date" ref="expected_delivery_date"
                name="expected_delivery_date" />
        </div>

        <div class="mb-4">
            <div class="d-flex justify-content-between align-items-center mb-2">
                <label class="form-label form-label-thin mb-0">Received By <span class="text-danger">*</span></label>
                <button type="button" class="btn btn-sm btn-link text-decoration-none text-primary p-0"
                    @click="openCreateUserModal('received')">
                    <i class="fa fa-plus-circle"></i> Add User
                </button>
            </div>
            <MultiSelect 
                v-model="form.received_by" 
                :options="memberOptions" 
                :multiple="true" 
                placeholder="Select Members" 
            />
            <div v-if="errors.received_by" class="text-danger small mt-1">
                {{ errors.received_by[0] || errors.received_by }}
            </div>
            <small class="text-muted">who will receive the purchase order</small>
        </div>

        <div class="mb-4">
            <div class="d-flex justify-content-between align-items-center mb-2">
                <label class="form-label form-label-thin mb-0">Final Verification By <span
                        class="text-danger">*</span></label>
                <button type="button" class="btn btn-sm btn-link text-decoration-none text-primary p-0"
                    @click="openCreateUserModal('verify')">
                    <i class="fa fa-plus-circle"></i> Add User
                </button>
            </div>
            <MultiSelect 
                v-model="form.final_verification_by" 
                :options="memberOptions" 
                :multiple="true" 
                placeholder="Select Members" 
            />
            <div v-if="errors.final_verification_by" class="text-danger small mt-1">
                {{ errors.final_verification_by[0] || errors.final_verification_by }}
            </div>
            <small class="text-muted">who will verify the purchase order</small>
        </div>

        <FormModal ref="createUserModal" title="Create New User" id="create_user_form_modal">
            <CreateUser @created="onUserCreated" @close="closeCreateUserModal" />
        </FormModal>

        <div class="mb-4">
            <FileUploader v-model:files="form.files" :help-text="'Attach order-related files or receipts.'" />
        </div>

        <div class="d-flex justify-content-end gap-2">
            <button type="button" class="btn btn-sm btn-outline-secondary" @click="close">
                <i class="fa fa-times"></i> Cancel
            </button>
            <button type="submit" class="btn btn-primary py-1 px-2 text-light hover-white btn-sm form-submit-btn"
                :disabled="submitting">
                <i class="fa-solid fa-plus fs-12"></i> {{ submitting ? 'Creating...' : 'Create Order' }}
            </button>
        </div>
    </form>
</template>

<script>
import FileUploader from '@/components/FileUploader.vue'
import { form_api_submit } from '@/utils/FormSubmit'
import CreateUser from '@/views/User/CreateUser.vue'
import FormModal from '@/components/utils/FormModal.vue'
import CreateVendor from '@/views/Vendor/CreateVendor.vue'
import MultiSelect from '@/components/MultiSelect.vue'

export default {
    name: 'CreateOrder',
    components: {
        FormModal,
        CreateUser,
        CreateVendor,
        MultiSelect,
        FileUploader,
    },
    emits: ['created', 'close'],
    data() {
        return {
            form: {
                vendor_id: null,
                description: '',
                received_by: [],
                final_verification_by: [],
                expected_delivery_date: '',
                items: [
                    {
                        item_id: '',
                        quantity: 1,
                        unit: 'Pcs',
                        unit_price: 0,
                        total: 0,
                        material_type: '',
                        sds: 'no',
                        approved_chemical: 'no',
                        food_grade_chemical: 'no',
                        food_contact_part: 'no'
                    }
                ],
                files: [],
            },
            vendors: [],
            members: [],
            vendorMaterials: [],
            units: ['Pcs', 'Kg', 'Ltr', 'Box', 'Roll'],
            errors: {},
            submitting: false,
            loading: false,
            selectedUserType: null,
        }
    },
    computed: {
        orderTotal() {
            return this.form.items.reduce((sum, item) => sum + Number(item.total || 0), 0)
        },
        vendorOptions() {
            return this.vendors.map(vendor => ({
                value: vendor.id,
                label: vendor.name || vendor.first_name || vendor.company_name || 'Vendor',
            }))
        },
        memberOptions() {
            return this.members.map(member => ({
                value: member.id,
                label: `${member.first_name} ${member.last_name}`,
            }))
        },
        vendorMaterialOptions() {
            return this.vendorMaterials.map(material => ({
                value: material.id,
                label: material.name || material.description || material.external_id || `Item ${material.id}`
            }))
        }
    },
    async mounted() {
        await this.fetchVendors()
        // await this.fetchDropdown()
        await this.getMembers()
        this.form.items.forEach(item => this.updateItemTotal(item))
    },
    methods: {
        addItem() {
            this.form.items.push({
                item_id: '',
                quantity: 1,
                unit: 'pcs',
                unit_price: 0,
                total: 0,
                material_type: '',
                sds: 'no',
                approved_chemical: 'no',
                food_grade_chemical: 'no',
                food_contact_part: 'no'
            })
        },
        removeItem(index) {
            if (this.form.items.length === 1) return
            this.form.items.splice(index, 1)
        },
        updateItemTotal(item) {
            const quantity = Number(item.quantity || 0)
            const price = Number(item.unit_price || 0)
            item.total = Number((quantity * price).toFixed(2))
        },
        formatTotal(value) {
            return Number(value || 0).toFixed(2)
        },
        onItemSelected(item, value) {
            item.item_id = value
            const res = this.findMaterialById(value)
            const material = res?.found || null
            const typeValue = (material?.type || material?.material_type || '').toString().trim().toLowerCase()

            item.material_type = typeValue || ''

            if (item.material_type !== 'chemical') {
                item.sds = 'no'
                item.approved_chemical = 'no'
                item.food_grade_chemical = 'no'
            }
            if (item.material_type !== 'part') {
                item.food_contact_part = 'no'
            }
            // debug: uncomment if you need to see selection details
            // console.debug('onItemSelected', value, typeValue, item.material_type)
        },
        findMaterialById(id) {
            if (!id) return null
            const found = this.vendorMaterials.find(material => String(material.id) === String(id) || String(material.external_id) === String(id))
            return found ? { found, categoryName: found.type } : null
        },
        async handleSubmit() {
          
            this.submitting = true
            this.errors = {}

            const formData = new FormData()
            formData.append('vendor_id', this.form.vendor_id)
            formData.append('description', this.form.description || '')
            formData.append('expected_delivery_date', this.form.expected_delivery_date || '')
            if (Array.isArray(this.form.received_by)) {
                this.form.received_by.forEach(value => {
                    formData.append('received_by[]', value)
                })
            } else if (this.form.received_by) {
                formData.append('received_by[]', this.form.received_by)
            }

            if (Array.isArray(this.form.final_verification_by)) {
                this.form.final_verification_by.forEach(value => {
                    formData.append('final_verification_by[]', value)
                })
            } else if (this.form.final_verification_by) {
                formData.append('final_verification_by[]', this.form.final_verification_by)
            }

            this.form.items.forEach((item, index) => {
                formData.append(`items[${index}][item_id]`, item.item_id)
                formData.append(`items[${index}][quantity]`, item.quantity)
                formData.append(`items[${index}][unit]`, item.unit)
                formData.append(`items[${index}][unit_price]`, item.unit_price)
                formData.append(`items[${index}][total]`, item.total)
                formData.append(`items[${index}][material_type]`, item.material_type || '')
                formData.append(`items[${index}][sds]`, item.sds || 'no')
                formData.append(`items[${index}][approved_chemical]`, item.approved_chemical || 'no')
                formData.append(`items[${index}][food_grade_chemical]`, item.food_grade_chemical || 'no')
                formData.append(`items[${index}][food_contact_part]`, item.food_contact_part || 'no')
            })

            this.form.files.forEach(file => {
                formData.append('files[]', file)
            })

            try {
              const result = await form_api_submit({
                url: 'purchase-order/create',
                method: 'POST',
                data: formData,
                curr_form_id: '#order_create_form',
                take_form_data: false
            })

            if (result.success) {
                this.$emit('created', result.data)
                this.close()
            } else if (result.errors) {
                this.errors = result.errors
            }

            } catch (error) {
                console.error('Error creating order:', error)
            } finally {
                this.submitting = false
            }
        },
        close() {
            this.$emit('close')
        },
        resetForm() {
            this.form = {
                vendor_id: null,
                description: '',
                received_by: [],
                final_verification_by: [],
                expected_delivery_date: '',
                items: [
                    {
                        item_id: '',
                        quantity: 1,
                        unit: 'pcs',
                        unit_price: 0,
                        total: 0,
                        material_type: '',
                        sds: 'no',
                        approved_chemical: 'no',
                        food_grade_chemical: 'no',
                        food_contact_part: 'no'
                    }
                ],
                files: [],
            }
            this.errors = {}
        },

        // async fetchDropdown() {
        //     this.loading = true
        //     try {
        //         const result = await form_api_submit({
        //             url: 'materials/dropdown',
        //             method: 'GET',
        //             data: {},
        //             show_success_toast: false,
        //             show_error_toast: true,
        //             remove_pre_toasts: false
        //         })

        //         if (result.success) {
        //             this.dropdownCategories = result.data || []
        //         }
        //     } catch (error) {
        //         console.error('Error fetching dropdown:', error)
        //     } finally {
        //         this.loading = false
        //     }
        // },

        // Vendor methods
        openCreateVendorModal() {
            this.$refs.createVendorModal.showModal()
        },

        closeCreateVendorModal() {
            this.$refs.createVendorModal.closeModal()
        },

        async onVendorCreated(vendorData) {
            await this.fetchVendors()
            this.closeCreateVendorModal()

            if (vendorData && vendorData.vendor && vendorData.vendor.external_id) {
                const newVendor = this.vendors.find(v => v.external_id === vendorData.vendor.external_id)
                if (newVendor) {
                    this.form.vendor_id = newVendor.id
                }
            }
        },

        // User methods
        openCreateUserModal(type) {
            this.selectedUserType = type // 'received' or 'verify'
            this.$refs.createUserModal.showModal()
        },

        closeCreateUserModal() {
            this.$refs.createUserModal.closeModal()
        },

        async onUserCreated(userData) {
            await this.getMembers()
            this.closeCreateUserModal()

            if (userData && userData.user && userData.user.external_id) {
                const newUser = this.members.find(m => m.external_id === userData.user.external_id)
                if (newUser) {
                    if (this.selectedUserType === 'received') {
                        this.form.received_by = Array.isArray(this.form.received_by) ? [...new Set([...(this.form.received_by || []), newUser.id])] : [newUser.id]
                    } else if (this.selectedUserType === 'verify') {
                        this.form.final_verification_by = Array.isArray(this.form.final_verification_by) ? [...new Set([...(this.form.final_verification_by || []), newUser.id])] : [newUser.id]
                    }
                }
            }
        },

        async getMembers() {
            try {
                const result = await form_api_submit({
                    url: 'user/dropdown',
                    method: 'GET',
                    data: {},
                    show_success_toast: false,
                    show_error_toast: true,
                })

                if (result.success) {
                    this.members = result.data || []
                }
            } catch (error) {
                console.error('Error fetching members:', error)
            }
        },

        async fetchVendors() {
            try {
                const result = await form_api_submit({
                    url: 'vendor/get-vendors',
                    method: 'GET',
                    show_success_toast: false,
                    show_error_toast: false,
                })

                if (result?.success) {
                    this.vendors = (result.data || []).map(vendor => ({
                        id: vendor.id,
                        external_id: vendor.external_id,
                        name: vendor.first_name || vendor.get_profile?.company_name || 'Vendor',
                        first_name: vendor.first_name,
                        company_name: vendor.get_profile?.company_name,
                    }))
                }
            } catch (error) {
                console.error('Failed to load vendors', error)
            }
        },
        async fetchVendorMaterials(externalId) {
            if (!externalId) {
                this.vendorMaterials = []
                return
            }

            this.loading = true
            try {
                const result = await form_api_submit({
                    url: `vendor/supplies/${externalId}`,
                    method: 'GET',
                    data: {},
                    show_success_toast: false,
                    show_error_toast: true,
                    remove_pre_toasts: false
                })

                if (result?.success && result.data?.vendor?.materials) {
                    this.vendorMaterials = result.data.vendor.materials
                } else {
                    this.vendorMaterials = []
                }
            } catch (error) {
                console.error('Error fetching vendor supplies:', error)
                this.vendorMaterials = []
            } finally {
                this.loading = false
            }
        },
        groupMaterialsByType(materials) {
            const groups = {}
            materials.forEach(material => {
                const type = String(material.type || 'other').toLowerCase()
                if (!groups[type]) groups[type] = []

                groups[type].push({
                    value: material.id,
                    label: material.name || material.description || `Item ${material.id}`
                })
            })

            return Object.keys(groups).map(type => ({
                label: this.capitalize(type) + (type.endsWith('s') ? '' : 's'),
                options: groups[type]
            }))
        },
        clearOrderItems() {
            this.form.items.forEach(item => {
                item.item_id = ''
                item.material_type = ''
                item.sds = 'no'
                item.approved_chemical = 'no'
                item.food_grade_chemical = 'no'
                item.food_contact_part = 'no'
                item.total = 0
            })
        },
        async onVendorSelected(value) {
            this.form.vendor_id = value
            const selectedVendor = this.vendors.find(vendor => String(vendor.id) === String(value))
            const externalId = selectedVendor?.external_id
            this.clearOrderItems()
            await this.fetchVendorMaterials(externalId)
        },
    }
}
</script>

<style scoped>
.contact-card {
    border-radius: 0.375rem;
    transition: all 0.2s ease-in-out;
}

.row {
    display: flex;
    flex-wrap: wrap;
    margin: -0.5rem;
}

.col-md-6 {
    flex: 0 0 50%;
    padding: 0.5rem;
}

.order-total-display {
    background: #f8f9fa;
    border-radius: 6px;
    border: 1px solid rgba(0, 0, 0, 0.05);
}

/* Compact field styling for per-item material flags */
.compact-field .form-label {
    margin-top: 0.25rem;
    margin-bottom: 0.25rem;
    font-size: 13px;
    line-height: 1.1;
}
.compact-field .form-select {
    margin-top: 0;
    margin-bottom: 0.25rem;
}
.compact-row {
    display: flex;
    gap: 0.75rem;
    flex-wrap: wrap;
}
.compact-row .col-md-4 {
    flex: 1 1 0;
    max-width: 33.333%;
}


@media (max-width: 768px) {
    .col-md-6 {
        flex: 0 0 100%;
    }
}
</style>